/**
 * ListingPage.duck.js
 * - Adds bid existence check (checkBidExistenceThunk / verifyBidExistence)
 * - Adds bid submit flow (submitBidTransactionThunk / submitBidTransaction)
 * - Fixes state naming consistency (placeBidInProgress/placeBidError exist in initialState)
 * - Adds clear console logs in BOTH thunks so you can confirm they’re hit
 *
 * NOTE:
 * - `listingId` passed to Flex SDK is usually an SDK UUID object (new UUID(...)).
 *   The transaction id is a UUID object too.
 * - In submitBidTransactionThunk we pass listingId to your own endpoints as-is;
 *   keep it consistent with what your backend expects (uuid string vs UUID object).
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  initiateWithWorkflowSnapshot,
  notifyOfferEvent,
  transactionLineItems,
} from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import {
  hasPermissionToInitiateTransactions,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';
import { fetchCurrentUser, setCurrentUserHasOrders } from '../../ducks/user.duck';
import { initiateBiddingPrivileged, updateListingBids } from '../../util/api';
import { transitions } from '../../transactions/transactionProcessNegotiation';
import moment from 'moment';
import { SHIPPING } from '../../util/constants';
import { isArrayLength } from '../../util/genericHelpers';
import { OfferTransitions } from '../../util/enums';

const { UUID } = sdkTypes;
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================= Selectors ================= //

export const bidExistenceSelector = state => {
  const { bidExistenceInProgress, bidExistenceError, isBidAlreadyPlaced } = state.ListingPage;
  return {
    bidExistenceInProgress,
    bidExistenceError,
    isBidAlreadyPlaced,
  };
};

export const bidTransactionSelector = state => {
  const { placeBidInProgress, placeBidError } = state.ListingPage;
  return {
    placeBidInProgress,
    placeBidError,
  };
};

export const sendOfferInProgressSelector = state => state.ListingPage.sendOfferInProgress;
export const sendOfferErrorSelector = state => state.ListingPage.sendOfferError;

// ================= Thunks: Bid ================= //

export const submitBidTransactionThunk = createAsyncThunk(
  'ListingPage/submitBidTransaction',
  async (params, { rejectWithValue, extra: sdk, dispatch }) => {
    try {
      const {
        listingId,
        processAlias,
        deliveryMethod = SHIPPING,
        quantity = 1,
        auctionEndUnix,
        biddingAmount,
        biddingCurrency,
        bidderId,
        biddrName,
        biddingTime,
        ...rest
      } = params || {};

      if (!listingId || !processAlias) throw new Error('Missing listingId or processAlias');
      if (!biddingAmount || !biddingCurrency)
        throw new Error('Missing biddingAmount or biddingCurrency');
      if (!auctionEndUnix) throw new Error('Missing auctionEndUnix');

      const nowUnix = moment().unix();
      if (auctionEndUnix <= nowUnix) {
        throw new Error('Timed Sale has already ended');
      }

      // Round auction end to nearest 15 min boundary (up)
      let auctionTime = moment.unix(auctionEndUnix);
      const roundedMinutes = Math.ceil(auctionTime.minute() / 15) * 15;
      auctionTime = auctionTime.minute(roundedMinutes).second(0);

      const bookingStart = auctionTime.toDate();
      const bookingEnd = auctionTime
        .clone()
        .add(14, 'days')
        .toDate();

      const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
      const orderData = deliveryMethod ? { deliveryMethod } : {};
      const listingUuid =
        typeof listingId === 'string'
          ? listingId
          : listingId?.uuid || listingId?.id?.uuid || listingId?.toString?.();

      const biddingTimeUnix = Number.isFinite(biddingTime) ? biddingTime : moment().unix();
      const protectedData = {
        isBidding: true,
        bidderId,
        biddrName,
        biddingAmount,
        biddingCurrency,
        biddingTime: biddingTimeUnix,
      };

      const transitionParams = {
        ...quantityMaybe,
        bookingStart,
        bookingEnd,
        listingId: listingUuid || listingId,
        isBidding: true,
        biddingAmount,
        biddingCurrency,
        protectedData,
      };

      const bodyParams = {
        transition: transitions.PLACE_BID,
        processAlias,
        params: transitionParams,
      };

      const queryParams = { include: ['booking', 'provider'], expand: true };

      const apiResp = await initiateBiddingPrivileged({
        isSpeculative: false,
        queryParams,
        orderData,
        bodyParams,
      });

      const transaction = apiResp?.data?.data;
      if (!transaction?.id) throw new Error('Transaction initiate failed: missing transaction id');

      const txId = transaction.id?.uuid;

      const bidWithTransactionId = { ...rest, transactionId: txId };

      const biddingResponse = await updateListingBids({
        id: listingUuid || listingId,
        ...bidWithTransactionId,
        biddingAmount,
        biddingCurrency,
        bidderId,
        biddrName,
        biddingTime: biddingTimeUnix,
      });

      if (listingUuid) {
        try {
          const listingResponse = await sdk.listings.show({
            id: listingUuid,
            include: ['author', 'images', 'currentStock'],
          });
          dispatch(addMarketplaceEntities(listingResponse));
        } catch (error) {
          console.log('[submitBidTransactionThunk] listing refresh failed', error);
        }
      }

      return {
        ...biddingResponse,
        transactionId: txId,
        transaction,
      };
    } catch (e) {
      console.log('[submitBidTransactionThunk] error', e);
      return rejectWithValue(storableError(e));
    }
  }
);

// Wrapper (keep your existing call sites unchanged)
export const submitBidTransaction = params => (dispatch, getState, sdk) =>
  dispatch(submitBidTransactionThunk(params)).unwrap();

/**
 * Bid existence check:
 * IMPORTANT:
 * - This checks whether the CURRENT USER has any transaction for the given listing.
 * - The Flex SDK query filters can differ per version; safest approach:
 *    1) Query transactions of current user (customer) with include
 *    2) Filter by listing id in response if listingId param is not supported
 */
export const checkBidExistenceThunk = createAsyncThunk(
  'ListingPage/checkBidExistence',
  async ({ listingId }, { rejectWithValue, getState, extra: sdk }) => {
    try {
      const state = getState();
      const currentUserId = state.user?.currentUser?.id;

      if (!listingId) throw new Error('Missing listingId');
      if (!currentUserId) return false;

      // Try direct filter first
      let resp;
      try {
        resp = await sdk.transactions.query({
          listingId,
          perPage: 1,
          page: 1,
        });
      } catch (err) {
        // fallback: query without listingId filter (SDK/version differences)
        console.log('[checkBidExistenceThunk] listingId filter failed, fallback query', err);
        resp = await sdk.transactions.query({
          perPage: 10,
          page: 1,
        });
      }

      const txs = resp?.data?.data || [];
      // If the query did NOT filter by listingId, filter client-side:
      const listingUuid =
        typeof listingId === 'string'
          ? listingId
          : listingId?.uuid || listingId?.id?.uuid || listingId?.toString?.();

      const filtered = listingUuid
        ? txs.filter(t => {
            const lId = t?.relationships?.listing?.data?.id?.uuid;
            return lId === listingUuid;
          })
        : txs;

      const exists = isArrayLength(filtered);
      return exists;
    } catch (e) {
      console.log('[checkBidExistenceThunk] error', e);
      return rejectWithValue(storableError(e));
    }
  }
);

export const verifyBidExistence = listingId => (dispatch, getState, sdk) =>
  dispatch(checkBidExistenceThunk({ listingId })).unwrap();

// ================= Thunks: Listing / Reviews / Timeslots ================= //

const showListingPayloadCreator = ({ listingId, config, isOwn = false }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      'variants.facebook',
      'variants.twitter',

      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const showListingThunk = createAsyncThunk(
  'ListingPage/showListing',
  showListingPayloadCreator
);

export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) =>
  dispatch(showListingThunk({ listingId, config, isOwn })).unwrap();

export const fetchReviewsThunk = createAsyncThunk(
  'ListingPage/fetchReviews',
  ({ listingId }, { rejectWithValue, extra: sdk }) => {
    return sdk.reviews
      .query({
        listing_id: listingId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      })
      .then(response => denormalisedResponseEntities(response))
      .catch(e => rejectWithValue(storableError(e)));
  }
);

export const fetchReviews = listingId => (dispatch, getState, sdk) =>
  dispatch(fetchReviewsThunk({ listingId })).unwrap();

const timeSlotsRequest = createAsyncThunk(
  'ListingPage/timeSlotsRequest',
  (params, { extra: sdk }) =>
    sdk.timeslots.query(params).then(response => denormalisedResponseEntities(response))
);

const fetchTimeSlotsPayloadCreator = ({ listingId, start, end, timeZone, options }, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => response.payload)
      .catch(() => []);
  }

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(response => response.payload)
    .catch(() => []);
};

export const fetchTimeSlotsThunk = createAsyncThunk(
  'ListingPage/fetchTimeSlots',
  fetchTimeSlotsPayloadCreator
);

export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => dispatch(fetchTimeSlotsThunk({ listingId, start, end, timeZone, options })).unwrap();

const sendInquiryPayloadCreator = (
  { listing, message },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', { listingId: listing?.id?.uuid });
    return rejectWithValue(storableError(error));
  }

  const listingId = listing?.id;
  const [processName] = processAlias.split('/');
  const t = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: t.INQUIRE,
    processAlias,
    params: { listingId },
  };

  return initiateWithWorkflowSnapshot({ bodyParams, queryParams: {} })
    .then(response => {
      const transactionId = response.data.data.id;
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(setCurrentUserHasOrders());
        return transactionId;
      });
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const sendInquiryThunk = createAsyncThunk(
  'ListingPage/sendInquiry',
  sendInquiryPayloadCreator
);

export const sendInquiry = (listing, message) => (dispatch, getState, sdk) =>
  dispatch(sendInquiryThunk({ listing, message })).unwrap();

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';

    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce(
      (min, priceVariant) => Math.min(min, priceVariant.bookingLengthInMinutes),
      Number.MAX_SAFE_INTEGER
    );

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign =>
      ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  return Promise.all([]);
};

// ================= Line items ================= //

const fetchTransactionLineItemsPayloadCreator = (
  { orderData, listingId, isOwnListing },
  { rejectWithValue }
) =>
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => response.data)
    .catch(e => {
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
      return rejectWithValue(storableError(e));
    });

export const fetchTransactionLineItemsThunk = createAsyncThunk(
  'ListingPage/fetchTransactionLineItems',
  fetchTransactionLineItemsPayloadCreator
);

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch =>
  dispatch(fetchTransactionLineItemsThunk({ orderData, listingId, isOwnListing })).unwrap();

// ================= Offer ================= //

export const sendOfferThunk = createAsyncThunk(
  'ListingPage/sendOffer',
  async (params, { rejectWithValue, dispatch, extra: sdk }) => {
    try {
      const { listing, price, quantity } = params || {};

      const listingId = listing?.id;
      const processAlias =
        listing?.attributes?.publicData?.transactionProcessAlias || 'default-negotiation/release-1';

      if (!listingId || !processAlias) {
        throw new Error('Listing is missing required transaction process alias');
      }

      const publicData = listing?.attributes?.publicData || {};
      const currentStock = listing?.currentStock?.attributes?.quantity;
      const moqRaw = publicData?.moq;
      const moq = Number.isInteger(moqRaw) ? moqRaw : Number.parseInt(moqRaw, 10);
      const quantityParsed = Number.isInteger(quantity) ? quantity : Number.parseInt(quantity, 10);
      const offerParams = {
        isOffer: true,
        price: price?.amount,
        currency: price?.currency,
        type: 'initial',
        createdAt: Math.floor(Date.now() / 1000),
        ...(Number.isInteger(quantityParsed) ? { quantity: quantityParsed } : {}),
        ...(Number.isInteger(moq) ? { moq } : {}),
        ...(Number.isInteger(currentStock) ? { stockAtOfferTime: currentStock } : {}),
      };

      const bodyParams = {
        transition: OfferTransitions.SEND_OFFER,
        processAlias,
        params: {
          listingId,
          protectedData: { offerParams },
        },
      };

      const resp = await initiateWithWorkflowSnapshot({ bodyParams, queryParams: {} });
      const transaction = resp?.data?.data;

      dispatch(setCurrentUserHasOrders());
      if (transaction?.id?.uuid) {
        notifyOfferEvent({ transactionId: transaction.id.uuid }).catch(() => {});
      }
      return transaction;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const sendOffer = params => (dispatch, getState, sdk) =>
  dispatch(sendOfferThunk(params)).unwrap();

// ================= Slice ================= //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,

  monthlyTimeSlots: {},
  timeSlotsForDate: {},

  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,

  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  offerModalOpenForListingId: null,
  bidModalOpenForListingId: null,

  bidExistenceInProgress: false,
  bidExistenceError: null,
  isBidAlreadyPlaced: false,

  // IMPORTANT: keep names consistent with selectors
  placeBidInProgress: false,
  placeBidError: null,

  sendOfferInProgress: false,
  sendOfferError: null,
};

const listingPageSlice = createSlice({
  name: 'ListingPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      // Offer
      .addCase(sendOfferThunk.pending, state => {
        state.sendOfferInProgress = true;
        state.sendOfferError = null;
      })
      .addCase(sendOfferThunk.fulfilled, state => {
        state.sendOfferInProgress = false;
      })
      .addCase(sendOfferThunk.rejected, (state, action) => {
        state.sendOfferInProgress = false;
        state.sendOfferError = action.payload;
      })

      // Bid existence
      .addCase(checkBidExistenceThunk.pending, state => {
        state.bidExistenceInProgress = true;
        state.bidExistenceError = null;
      })
      .addCase(checkBidExistenceThunk.fulfilled, (state, action) => {
        state.bidExistenceInProgress = false;
        state.isBidAlreadyPlaced = !!action.payload;
      })
      .addCase(checkBidExistenceThunk.rejected, (state, action) => {
        state.bidExistenceInProgress = false;
        state.bidExistenceError = action.payload;
      })

      // Submit bid
      .addCase(submitBidTransactionThunk.pending, state => {
        state.placeBidInProgress = true;
        state.placeBidError = null;
      })
      .addCase(submitBidTransactionThunk.fulfilled, state => {
        state.placeBidInProgress = false;
      })
      .addCase(submitBidTransactionThunk.rejected, (state, action) => {
        state.placeBidInProgress = false;
        state.placeBidError = action.payload;
      })

      // Listing
      .addCase(showListingThunk.pending, (state, action) => {
        state.id = action.meta.arg.listingId;
        state.showListingError = null;
      })
      .addCase(showListingThunk.fulfilled, () => {
        // handled by addMarketplaceEntities
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        state.showListingError = action.payload;
      })

      // Reviews
      .addCase(fetchReviewsThunk.pending, state => {
        state.fetchReviewsError = null;
      })
      .addCase(fetchReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(fetchReviewsThunk.rejected, (state, action) => {
        state.fetchReviewsError = action.payload;
      })

      // Time slots
      .addCase(fetchTimeSlotsThunk.pending, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          state.timeSlotsForDate = removeOutdatedDateData(state.timeSlotsForDate);
          if (!state.timeSlotsForDate[dateId]) state.timeSlotsForDate[dateId] = {};
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = null;
          state.timeSlotsForDate[dateId].fetchedAt = null;
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = true;
          state.timeSlotsForDate[dateId].timeSlots = [];
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) state.monthlyTimeSlots[monthId] = {};
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = null;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = true;
        }
      })
      .addCase(fetchTimeSlotsThunk.fulfilled, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) state.timeSlotsForDate[dateId] = {};
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchedAt = new Date().getTime();
          state.timeSlotsForDate[dateId].timeSlots = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) state.monthlyTimeSlots[monthId] = {};
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].timeSlots = action.payload;
        }
      })
      .addCase(fetchTimeSlotsThunk.rejected, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) state.timeSlotsForDate[dateId] = {};
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) state.monthlyTimeSlots[monthId] = {};
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = action.payload;
        }
      })

      // Inquiry
      .addCase(sendInquiryThunk.pending, state => {
        state.sendInquiryInProgress = true;
        state.sendInquiryError = null;
      })
      .addCase(sendInquiryThunk.fulfilled, state => {
        state.sendInquiryInProgress = false;
        state.inquiryModalOpenForListingId = null;
      })
      .addCase(sendInquiryThunk.rejected, (state, action) => {
        state.sendInquiryInProgress = false;
        state.sendInquiryError = action.payload;
      })

      // Line items
      .addCase(fetchTransactionLineItemsThunk.pending, state => {
        state.fetchLineItemsInProgress = true;
        state.fetchLineItemsError = null;
      })
      .addCase(fetchTransactionLineItemsThunk.fulfilled, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.lineItems = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.rejected, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.fetchLineItemsError = action.payload;
      });
  },
});

export const { setInitialValues } = listingPageSlice.actions;
export default listingPageSlice.reducer;

// ================= loadData ================= //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);
  const state = getState();
  const currentUser = state.user?.currentUser;

  const inquiryModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.inquiryModalOpenForListingId
      : null;
  const offerModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.offerModalOpenForListingId
      : null;
  const bidModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.bidModalOpenForListingId
      : null;

  // Clear old line-items
  dispatch(
    setInitialValues({
      lineItems: null,
      inquiryModalOpenForListingId,
      offerModalOpenForListingId,
      bidModalOpenForListingId,
    })
  );

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const promises = hasNoViewingRights
    ? [dispatch(showListing(listingId, config, true))]
    : [dispatch(showListing(listingId, config)), dispatch(fetchReviews(listingId))];

  return Promise.all(promises).then(response => {
    const listingResponse = response[0];
    const listing = listingResponse?.data?.data;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';

    if (isBookingProcessAlias(transactionProcessAlias) && !hasNoViewingRights) {
      fetchMonthlyTimeSlots(dispatch, listing);
    }

    return response;
  });
};
